import React, {useRef, useEffect, useState, JSX} from 'react';
import {Animated, Easing, LayoutChangeEvent} from 'react-native';
import {isWeb, wdp} from '../../utils/responsive';
import {LinearGradient} from 'react-native-linear-gradient';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import styled from 'styled-components/native';
import {SkeletonPlaceholderProps} from '../../interfaces/components/skeletonPlaceholder';
import {isValidNumber} from '../../utils/helpers';

const Container = styled(Animated.View)<any>`
  width: 100%;
  height: 100%;
  overflow: hidden;
`;
const ContentContainer = styled(Animated.View)`
  width: ${wdp(60)}px;
  height: 200%;
  top: -50%;
  position: absolute;
`;

/**
 * SkeletonPlaceholder Component
 *
 * The SkeletonPlaceholder component is designed to create a shimmering placeholder effect,
 * commonly used to represent loading or placeholder content in a visually appealing way.
 *
 * @component
 *
 * @param {boolean} isOpacityEffect - Determines whether to use opacity effect instead of shimmer effect.
 * @param {number} borderRadius - Border radius of the placeholder container.
 * @param {number} width - Width of the placeholder. Default is '100%'.
 * @param {number | string} height - Height of the placeholder. If not provided, it defaults to '100%' or undefined if aspectRatio is used.
 * @param {number} marginBottom - Bottom margin of the placeholder container.
 * @param {number} marginTop - Top margin of the placeholder container.
 * @param {number} marginRight - Right margin of the placeholder container.
 * @param {number} marginLeft - Left margin of the placeholder container.
 * @param {number} borderTopLeft - Border top-left radius of the placeholder container.
 * @param {number} borderTopRight - Border top-right radius of the placeholder container.
 * @param {number} borderBottomLeft - Border bottom-left radius of the placeholder container.
 * @param {number} borderBottomRight - Border bottom-right radius of the placeholder container.
 * @param {number} aspectRatio - Aspect ratio of the placeholder. If provided, height will be undefined.
 *
 * @returns {JSX.Element} SkeletonPlaceholder component.
 */
const SkeletonPlaceholder = ({
  isOpacityEffect = false,
  borderRadius = 10,
  width,
  height,
  marginBottom = 0,
  marginTop = 0,
  marginRight = 0,
  marginLeft = 0,
  borderTopLeft,
  borderTopRight,
  borderBottomLeft,
  borderBottomRight,
  aspectRatio,
  backgroundColor,
}: SkeletonPlaceholderProps): JSX.Element => {
  const theme = useTheme() as Theme;
  const [viewWidth, setViewWidth] = useState(0);
  const shimmerValue = useRef(new Animated.Value(0)).current;
  const opacityValue = useRef(new Animated.Value(0)).current;

  useEffect(() => {}, []);
  const opacity = opacityValue.interpolate({
    inputRange: [0, 1],
    outputRange: [0.5, 0.8],
  });
  const handleLayout = (event: LayoutChangeEvent) => {
    const layoutWidth = event.nativeEvent.layout?.width;
    setViewWidth(layoutWidth);
  };

  const startShimmerAnimation = () => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(shimmerValue, {
          toValue: 1,
          duration: 2000,
          easing: Easing.linear,
          useNativeDriver: !isWeb,
        }),
        Animated.delay(500),
      ]),
    ).start();
  };
  const startOpacityAnimation = () => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(opacityValue, {
          toValue: 1,
          duration: 500,
          useNativeDriver: true,
        }),
        Animated.timing(opacityValue, {
          toValue: 0,
          duration: 500,
          useNativeDriver: true,
        }),
      ]),
    ).start();
  };

  useEffect(() => {
    if (isOpacityEffect) {
      startOpacityAnimation();
    } else {
      startShimmerAnimation();
    }
  }, [isOpacityEffect]);

  const interpolateShimmer = shimmerValue.interpolate({
    inputRange: [0, 1],
    outputRange: [-(viewWidth + wdp(60)), viewWidth + wdp(60)],
    extrapolate: 'clamp',
  });

  const getHeight = () => {
    if (height) {
      return height;
    }
    if (aspectRatio) {
      return undefined;
    }
    return '100%';
  };
  return (
    <Container
      style={{
        width: width || '100%',
        borderBottomLeftRadius: borderBottomLeft ?? borderRadius,
        borderBottomRightRadius: borderBottomRight ?? borderRadius,
        borderTopLeftRadius: borderTopLeft ?? borderRadius,
        borderTopRightRadius: borderTopRight ?? borderRadius,
        marginBottom,
        marginTop,
        marginRight,
        opacity,
        marginLeft,
        backgroundColor: backgroundColor || theme.skeletonBackground,
        ...(isValidNumber(aspectRatio)
          ? {aspectRatio, height: undefined}
          : {height: getHeight() || '100%'}),
      }}
      onLayout={handleLayout}>
      <ContentContainer
        style={{
          transform: [{translateX: interpolateShimmer}, {rotate: '-35deg'}],
        }}>
        <LinearGradient
          style={{
            height: '100%',
            width: '100%',
            zIndex: 99,
          }}
          start={{x: 0, y: 0}}
          end={{x: 1, y: 0}}
          colors={[
            'rgba(255,255,255,0)',
            isWeb ? theme.skeletonShimmer + '44' : theme.skeletonShimmer + 'AA',
            'rgba(255,255,255,0)',
          ]}
        />
      </ContentContainer>
    </Container>
  );
};

export default React.memo(SkeletonPlaceholder);
